<template>
  <div
    :class="{
      'document-setup': true,
      'document-setup--edit-mode': editMode,
    }"
    v-loading="loading"
    @keyup.esc="cancelFieldPlacement"
    @click="resetActiveFieldIfClickIsOnDoc">
    <div
      class="document-setup__header"
      :class="{ 'document-setup__header--envelope': isEnvelope }"
      @mousedown.capture="closeFieldConfiguration"
    >
      <slot
        name="controls"
        v-bind:currentDocIndex="currentDocIndex"
        v-bind:previousDocument="previousDocument"
        v-bind:nextDocument="nextDocument"
      >
        <VButton
          class="document-setup__header__back-button"
          :iconOnly="true"
          :rounded="true"
          type="outline-1"
          :to="{ name: 'EnvelopeCreate', params: { envelopeId: doc.fingerprint }}"
          v-if="isEnvelope"
          data-ec="document_setup"
          data-et="back_button_envelope">
          <ArrowLeftIcon />
        </VButton>

        <VButton
          class="document-setup__header__back-button"
          :iconOnly="true"
          :rounded="true"
          type="outline-1"
          :to="{ name: 'Template', params: { templateId: doc.fingerprint }}"
          v-if="!isEnvelope"
          data-ec="document_setup"
          data-et="back_button_template">
          <ArrowLeftIcon />
        </VButton>


        <VButton
          type="cta-3"
          class="float-right document-setup__header__action-button"
          :action="sendEnvelope"
          :disabled="!sendEnvelope"
          v-if="isEnvelope"
          data-ec="document_setup"
          data-et="send_now_button_envelope">
          <PaperPlaneIcon />
          <span>{{ $t('createEnvelope.documentSetup.sendNow') }}</span>
        </VButton>

        <VButton
          :href="`${signingPageUrl}/preview/${doc.fingerprint}/${doc.parties[0].status.fingerprint}#${previewKey}`"
          target="_blank"
          type="outline-1"
          :title="$t('functional.preview')"
          v-if="isEnvelope && previewKey"
          class="float-right document-setup__header__action-button"
          data-ec="document_setup"
          data-et="preview_button">
          <EyeIcon />
          <span>{{ $t('functional.preview') }}</span>
        </VButton>

        <span class="float-right document-setup__header__updated" v-if="doc.updated_at">
          {{ $t('createEnvelope.documentSetup.lastUpdated') }}<br/>
          <VMomentRefresh :timestamp="doc.updated_at" />
        </span>
        <h2>
          <span class="document-setup__header__title" v-if="!isEnvelope">
            <VInputEdit
              @saved="saveNewTitle"
              :text="doc.title"
              data-hj-suppress
              data-ec="document_setup"
              data-et="template_title_input"
            />
          </span>
          <span class="document-setup__header__title" v-else>
            {{ doc.title }}
          </span>
        </h2>
        <div class="document-setup__envelope-nav" v-if="isEnvelope && doc.documents.length > 1">
          <VButton
            class="document-setup__envelope-nav__icon"
            :disabled="currentDocIndex <= 0"
            type="cta-1"
            rounded
            iconOnly
            data-direction="prev"
            @click="previousDocument"
          >
            <ChevronLeftIcon />
          </VButton>
          <span class="document-setup__envelope-nav__text">
            {{ $t('createEnvelope.documentSetup.documentOfDocuments', { current: (currentDocIndex + 1), total: doc.documents.length }) }}
          </span>
          <VButton
            class="document-setup__envelope-nav__icon"
            :disabled="((currentDocIndex + 1) >= doc.documents.length)"
            type="cta-1"
            rounded
            iconOnly
            data-direction="next"
            @click="nextDocument"
          >
            <ChevronRightIcon />
          </VButton>
        </div>
      </slot>
    </div>
    <div class="document-setup__container">

      <!-- Select parties on the left-hand side -->
      <DocumentSetupParties
        :isEnvelope="isEnvelope"
        :fingerprint="doc.fingerprint"
        :selectedParty="selectedPartyWithFields"
        :parties="doc.parties"
        ec="document_setup"
        @select="selectParty"
      />
      <div class="document-setup__content" @click.self="cancelFieldPlacement">
        <Transition name="fade">
          <div
            class="document-setup__field-control"
            v-if="editMode"
            @click="cancelFieldPlacement"
            data-ec="document_setup"
            data-et="cancel_field_placement_button"
          >
            <BanIcon
              height="18"
              width="18"
            />
            <div class="document-setup__field-control__name document-setup__field-control__name--right">
              {{ $t('functional.cancel') }}
            </div>
          </div>
        </Transition>
        <Transition name="fade">
          <div
            class="document-setup__field-control"
            v-if="showConfigureField"
            @click="deleteField"
            data-ec="document_setup"
            data-et="delete_field_button"
          >
            <TrashIcon
              height="18"
              width="18"
            />
            <div class="document-setup__field-control__name document-setup__field-control__name--right">
              {{ $t('functional.delete') }}
            </div>
          </div>
        </Transition>

        <Transition name="slide">
        <div
          class="document-setup__pages"
          :class=" { 'document-setup__pages--push-up': showConfigureField }"
          @mousedown.capture="closeFieldConfiguration"
        >
          <div
            v-if="currentDoc && currentDoc.fingerprint"
            class="document-setup__page"
            id="page"
            ref="page"
            @click="addField"
            :data-fingerprint="currentDoc.fingerprint"
          >
            <VLoadingSpinner v-if="documentPageLoading && !loading" class="document-setup__page-spinner" />

            <div class="document-setup__page__snap-control">
              <span class="document-setup__page__snap-control__label">{{ $t('createEnvelope.documentSetup.snapMode')}}</span>
              <VCheckbox
                type="checkbox"
                subtype="toggle"
                size="small"
                :checked="snapMode"
                v-model="snapMode"
                data-ec="document_setup"
                data-et="snap_mode_toggle"
              />
            </div>

            <div
              class="document-setup__image"
              @mouseenter.prevent="setBoundingClientRect"
              @mousedown.prevent="mousedown"
              @mousemove.prevent="mousemove"
            >
              <div
                v-if="!documentPageLoading"
                class="document-setup__field-container"
                data-test="field_container"
                @contextmenu="openFieldContextMenu"
                @click.left="closeFieldContextMenuIfActive"
              >
                <FieldContextMenu
                  ref="fieldContextMenu"
                  v-if="fieldContextMenuActive"
                  @closeFieldContextMenu="closeFieldContextMenu"
                  @pasteField="pasteFromContextMenu"
                  @copyField="copyField"
                  @deleteField="deleteField"
                  @cutField="cutField"
                />

                <VueDraggableResizable
                  v-for="field in filteredAddedFields"
                  class="document-setup__page__field document-setup__page__field--loading"
                  :data-type="field.type"
                  :data-id="field.id"
                  :minh="20"
                  :minw="20"
                  :grid="snapGrid"
                  :parent="true"
                  :key="field.id"
                  :w="pixelValues(field, 'width') || defaultFieldValues(field, 'width')"
                  :h="pixelValues(field, 'height') || defaultFieldValues(field, 'height')"
                  :x="field.x || pixelValues(field, 'left')"
                  :y="field.y || pixelValues(field, 'top')"
                >
                  <span class="document-setup__page__field__adding-label">
                    {{ $t('createEnvelope.documentSetup.addingField') }}
                  </span>
                  <span class="document-setup__page__field__label">
                    {{ $t('createEnvelope.documentSetup.fieldLabel', { partyTitle: 'party', partyNumber: (field.party.order + 1), fieldType: field.type }) }}
                  </span>
                  <div v-if="field.required">
                    <ElTooltip class="item" effect="dark" :content="$t('createEnvelope.documentSetup.fieldRequired')" placement="top-start">
                      <div class="document-setup__page__field__required-marker"></div>
                    </ElTooltip>
                  </div>
                  <div v-if="field.type === 'checkbox' && field.group">
                    <ElTooltip class="item" effect="dark" :content="$t('createEnvelope.documentSetup.fieldConfiguration.group', { group: field.group })" placement="top-start">
                      <div class="document-setup__page__field__checkbox-group-marker"></div>
                      <CheckIcon />
                    </ElTooltip>
                  </div>
                </VueDraggableResizable>

                <VueDraggableResizable
                  v-for="field in conditionallyFilteredFields"
                  class="document-setup__page__field"
                  :ref="`field_${field.id}`"
                  :parent="true"
                  :data-type="field.type"
                  :key="`${field.id}w${pageSize.width}`"
                  :x="pixelValues(field, 'left')"
                  :y="pixelValues(field, 'top')"
                  :w="pixelValues(field, 'width')"
                  :h="pixelValues(field, 'height')"
                  :minh="20"
                  :minw="20"
                  :grid="snapGrid"
                  @activated="setConfigureField(field, $event)"
                  @dragging="onDrag"
                  @dragstop="onDragStop"
                  @resizing="onResizing"
                  @resizestop="onResizeStop"
                  :style="`${fieldOpacityStyle(field)}`"
                  :resizable="true"
                  data-field
                >
                  <span class="document-setup__page__field__label">
                    {{ $t('createEnvelope.documentSetup.fieldLabel', { partyTitle: field.party.title , partyNumber: (field.party.order + 1), fieldType: field.type }) }}
                  </span>
                  <div v-if="field.required">
                    <ElTooltip class="item" effect="dark" :content="$t('createEnvelope.documentSetup.fieldRequired')" placement="top-start">
                      <div class="document-setup__page__field__required-marker"></div>
                    </ElTooltip>
                  </div>
                  <div v-if="field.group && field.type === 'checkbox'">
                    <ElTooltip class="item" effect="dark" :content="$t('createEnvelope.documentSetup.fieldConfiguration.group', { group: field.group })" placement="top-start">
                      <div class="document-setup__page__field__checkbox-group-marker"></div>
                    </ElTooltip>
                  </div>
                </VueDraggableResizable>
              </div>

              <div
                v-show="editMode"
                class="document-setup__page__hover-field"
                :class="`document-setup__page__hover-field--${selectedField.type}`"
                ref="hoverField"
                :style="`
                  top: ${selectedField.y}px;
                  left: ${selectedField.x}px;
                  ${pointerEventsStyle}`"
                :data-type="selectedField.type"
              />
              <VImage
                @loaded="documentPageLoaded"
                @errored="documentPageErrored"
                :src="currentDoc.images[currentPage]"
                :key="`page-${currentDocIndex}-${currentPage}`"
                :error="require('@/assets/7x10-white.png')"
                ref="pageImage"
                data-hj-suppress
              />
            </div>
          </div>
          <div class="document-setup__navigation">
            <div v-if="currentDoc" class="document-setup__navigation__images">
              <button
                v-for="page in currentDoc.total_pages"
                class="document-setup__navigation__image"
                :class="{ 'document-setup__navigation__image--active': currentPage === (page - 1) }"
                :key="currentDoc+page"
                :disabled="page - 1 === currentPage"
                @click="changePage(page - 1)"
                data-ec="document_setup"
                data-et="change_document_page_button"
              >
                <VImage
                  :src="currentDoc.images[page - 1]"
                  :error="require('@/assets/loading.png')"
                  data-hj-suppress
                />
              </button>
            </div>

          </div>
        </div>
        </Transition>
        <!-- Field Editor -->
        <Transition name="slide">
          <VFieldConfiguration
            class="document-setup__field-config"
            v-if="showConfigureField"
            :show="showConfigureField"
            :field="configureField"
            :parties="doc.parties"
            :isEnvelope="isEnvelope"
            @updated="updatedConfiguration"
          />
        </Transition>
      </div>
      <!-- Input selection on the right-hand side -->
      <DocumentSetupFields ec="document_setup" :inputTypes="inputTypes" :selected="selectedInputType" @select="selectInputType" :enabled="hasPartySelected && selectedParty.role !== 'copy'" />

    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { throttle } from 'lodash';

import VButton from '@/components/deprecated/atoms/Button.vue';
import VCheckbox from '@/components/deprecated/atoms/Checkbox.vue';
import VLoadingSpinner from '@/components/deprecated/atoms/LoadingSpinner.vue';
import { logAmplitudeEvent } from '@/utils/analytics';
import VImage from '@/components/atoms/Image.vue';
import VInputEdit from '@/components/molecules/InputEdit.vue';
import DocumentSetupParties from '@/views/components/DocumentSetupParties.vue';
import DocumentSetupFields from '@/views/components/DocumentSetupFields.vue';
import VueDraggableResizable from '@/components/vue-draggable-resizable.vue';
import VFieldConfiguration from '@/views/components/FieldConfiguration.vue';
import VMomentRefresh from '@/views/components/MomentRefresh.vue';
import FieldContextMenu from '@/views/components/FieldContextMenu.vue';
import {
  ArrowLeftIcon,
  BanIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EyeIcon,
  PaperPlaneIcon,
  TrashIcon,
} from '@/components/icons/fontIcons';

export default {
  name: 'VDocumentSetup',
  props: {
    doc: {
      type: [Object],
      required: true,
    },
    fields: {
      type: [Array],
      required: true,
    },
    send: {
      type: [Function, Boolean],
      default: false,
    },
  },
  components: {
    VButton,
    VCheckbox,
    VLoadingSpinner,
    VFieldConfiguration,
    VInputEdit,
    DocumentSetupParties,
    DocumentSetupFields,
    VueDraggableResizable,
    VMomentRefresh,
    VImage,
    FieldContextMenu,
    ArrowLeftIcon,
    BanIcon,
    CheckIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    EyeIcon,
    PaperPlaneIcon,
    TrashIcon,
  },
  data() {
    return {
      temporaryFieldId: 1,
      fieldContextMenuActive: false,
      showFieldLabels: true,
      documentPageLoading: true,
      events: {
        mousedown: false,
        dragging: false,
        dragged: false,
      },
      inputTypes: [
        { type: 'text', typeName: this.$t('createEnvelope.documentSetup.inputType.text'), icon: 'font' },
        { type: 'signature', typeName: this.$t('createEnvelope.documentSetup.inputType.signature'), icon: 'pencil' },
        { type: 'date', typeName: this.$t('createEnvelope.documentSetup.inputType.date'), icon: 'calendar' },
        { type: 'dropdown', typeName: this.$t('createEnvelope.documentSetup.inputType.dropdown'), icon: 'list-ul' },
        { type: 'checkbox', typeName: this.$t('createEnvelope.documentSetup.inputType.checkbox'), icon: 'check-square' },
        { type: 'upload', typeName: this.$t('createEnvelope.documentSetup.inputType.upload'), icon: 'cloud-upload' },
      ],
      currentDocIndex: 0,
      currentPage: 0,
      currentDoc: {},
      editMode: false,
      activeFieldData: null,
      selectedField: {
        type: null,
        x: null,
        y: null,
      },
      pageRect: {
        x: null,
        y: null,
      },
      pageSize: {
        width: 0,
        height: 0,
      },
      throttlePaste: throttle(this.keyboardPaste, 250),
      selectedParty: {},
      selectedInputType: {},
      showConfigureField: false,
      configureField: {},
      addedFields: [],
      loading: true,
      renderVersion: 0,
      snapMode: false,
      signingPageUrl: process.env.VUE_APP_SIGNING_PAGE,
      pasteCascade: 2,
      currentFieldId: null,
      keyboardCopyPosition: null,
    };
  },
  created() {
    this.selectedParty = this.doc.parties[0] || {};
    if (this.doc.parties[0]) this.selectedParty.number = 1;

    // this.selectedInputType = this.inputTypes[0]; // Select the first one by default.

    this.$bus.$on('templatePartyDeleted', () => {
      this.selectedParty = this.doc.parties[0] || {};
      if (this.doc.parties[0]) this.selectedParty.number = 1;
    });

    this.$bus.$on('templatePartyAdded', () => {
      this.selectedParty = this.doc.parties[this.doc.parties.length - 1];
      this.selectedParty.number = this.doc.parties.length;
    });

    if (this.isEnvelope) {
      const [currentDoc] = this.doc.documents;
      this.currentDoc = currentDoc;
    } else {
      this.currentDoc = this.doc;
    }

    this.currentDocIndex = 0;
    window.addEventListener('mouseup', this.mouseup, false);
  },
  mounted() {
    if (!this.company) {
      this.$store.dispatch('company/GET');
    }
    this.$store.dispatch('company/GET_MERGE_FIELDS');
    this.$store.dispatch('company/GET_CHECKBOX_GROUPS');

    if (this.isEnvelope) this.$store.dispatch('auth/GET_PREVIEW_KEY');
    this.$nextTick(() => {
      window.addEventListener('resize', this.updatePageImageSize);
      window.addEventListener('copy', this.keyboardCopy);
      window.addEventListener('keydown', this.handleKeydown);
      window.addEventListener('paste', this.throttlePaste);
    });
  },
  updated() {
    if (!this.isEnvelope) this.currentDoc = this.doc;
  },
  beforeDestroy() {
    // we need to "autosave" field configuration before destroying this component
    this.closeFieldConfiguration();
  },
  destroyed() {
    if (this.clipboard) {
      this.$store.dispatch('fields/RESET_STATE', this.activeFieldData);
    }
    window.removeEventListener('resize', this.updatePageImageSize);
    window.removeEventListener('copy', this.keyboardCopy);
    window.removeEventListener('paste', this.throttlePaste);
    window.removeEventListener('keydown', this.handleKeydown);
  },
  computed: {
    ...mapGetters({
      clipboard: 'fields/clipboard',
      company: 'company/company',
      previewKey: 'auth/previewKey',
    }),
    snapGrid() {
      if (!this.snapMode) return undefined;
      return [10, 10];
    },
    hasPartySelected() {
      return Object.keys(this.selectedParty).length > 0;
    },
    selectedPartyWithFields() {
      const fields = this.fields.filter(field => field.party_id === this.selectedParty.id);
      return {
        fields,
        ...this.selectedParty,
      };
    },
    // Detect if the doc provided is an envelope or template
    isEnvelope() {
      return !!this.doc.documents;
    },
    // Used to target the correct store during VueX transactions
    storeName() {
      return this.isEnvelope ? 'envelopes' : 'template';
    },
    pointerEventsStyle() {
      const { editMode } = this;
      const { dragging, mousedown } = this.events;
      if (editMode || dragging || mousedown) {
        return 'pointer-events: none;';
      }
      return '';
    },
    filteredAddedFields() {
      return this.addedFields.filter(field => field.page === this.currentPage);
    },
    conditionallyFilteredFields() {
      return this.fields.filter(field => this.showFields(field));
    },
  },
  watch: {
    // remove pasteCascade on page change in order to paste in same location as field on previous page was copied from
    currentPage(newPage) {
      if (this.clipboard && newPage === this.clipboard.page) {
        this.pasteCascade += 2;
      } else {
        this.pasteCascade = 0;
      }
    },
  },
  methods: {
    handleKeydown(e) {
      if (e.code === 'Backspace') {
        this.keyboardDelete();
      }
      if (e.code === 'Delete') {
        this.keyboardDelete();
      }
    },
    keyboardDelete() {
      if (this.activeFieldData) {
        this.deleteField();
        this.fieldContextMenuActive = false;
        this.logEventToAmplitude('delete keyboard shortcut');
      }
    },
    keyboardCopy() {
      if (this.activeFieldData) {
        this.copyField();
        this.logEventToAmplitude('copy keyboard shortcut');
      }
    },
    keyboardPaste() {
      if (this.clipboard) {
        const keyboardCopyPosition = {
          top: `${parseFloat(this.clipboard.top, 10) + this.pasteCascade}%`,
          left: `${parseFloat(this.clipboard.left, 10) + this.pasteCascade}%`,
        };
        this.pasteField(keyboardCopyPosition);
        this.pasteCascade += 2;
        this.logEventToAmplitude('paste keyboard shortcut');
      }
    },
    resetActiveFieldIfClickIsOnDoc(e) {
      const clickOnField = 'field' in e.target.dataset;
      if (!clickOnField) {
        this.resetActiveField();
      }
    },
    resetActiveField() {
        this.activeFieldData = null;
    },
    openFieldContextMenu(e) {
      e.preventDefault();
      this.cancelFieldPlacement();
      this.fieldContextMenuActive = false;

      // Is right click on a field?
      // We added data-field to each vue-draggable-resizable component in
      // order to be able to determine if the target node is a field or not.
      // Much easier to use vanilla js in this instance due to the use of
      // mouse events.
      const menuOpenedOnField = 'field' in e.target.dataset;
      if (!menuOpenedOnField) {
        this.resetActiveField();
      }

      const originalMousePosition = {
        left: e.offsetX,
        top: e.offsetY,
      };

      let { left, top } = originalMousePosition;

      if (menuOpenedOnField) {
        /**
         * When the menu is opened on a field we use
         * the DOMNode's style properties to re-position
         * the context menu
         */
        const styles = getComputedStyle(e.target);
        const width = parseInt(styles.getPropertyValue('width'), 10);
        const height = parseInt(styles.getPropertyValue('height'), 10);
        left = parseInt(styles.getPropertyValue('left'), 10);
        top = parseInt(styles.getPropertyValue('top'), 10);
        left += width + 1; // Added a pixel to move clear of selection box
        top += height + 1; // Added a pixel to move clear of selection box
      }

      this.fieldContextMenuActive = true;

      this.$nextTick(() => {
        const styles = getComputedStyle(this.$refs.fieldContextMenu.$refs.fieldContextMenu.firstChild);
        const menuWidth = parseInt(styles.getPropertyValue('width'), 10);
        const menuHeight = parseInt(styles.getPropertyValue('height'), 10);

        if ((this.pageSize.width - left) < menuWidth) { // click location is too close to right edge - open menu left side of click location
          left -= menuWidth;
        }
        if ((this.pageSize.height - top) < menuHeight) { // click location is too close to bottom - open menu above click location
          top -= menuHeight;
        }

        this.closeFieldConfiguration();
        this.$refs.fieldContextMenu.initialiseContextMenu(menuOpenedOnField);
        this.$refs.fieldContextMenu.positionContextMenu(left, top, originalMousePosition);
      });
    },
    closeFieldContextMenu() {
      this.resetActiveField();
      this.fieldContextMenuActive = false;
    },
    closeFieldContextMenuIfActive() {
      if (this.fieldContextMenuActive) {
        this.closeFieldContextMenu();
      }
    },
    copyField() {
      if (this.activeFieldData) {
        // set keyboard copy position if the last copy was from context menu
        this.keyboardCopyPosition = {
          left: this.percentageToPixels(this.activeFieldData.left, this.pageSize.width),
          top: this.percentageToPixels(this.activeFieldData.top, this.pageSize.height),
        };
        this.logEventToAmplitude('copy');
        this.$store.dispatch('fields/COPY_FIELD', this.activeFieldData);
      }
    },
    logEventToAmplitude(eventName) {
      // detect if page is envelope or template
      const amplitudeTag = `${this.storeName}-${eventName}-field`;
      // TODO when keyboard shortcuts are added, detect here if event was click or shortcut
      logAmplitudeEvent(amplitudeTag);
    },
    pasteFromContextMenu(e) {
      const position = {
        left: this.pixelToPercentage(e.left, this.pageSize.width),
        top: this.pixelToPercentage(e.top, this.pageSize.height),
      };
      this.pasteField(position);
    },
    pasteField(position) {
      this.logEventToAmplitude('paste');
      let pasteCoordinates = {
        left: `${(parseFloat(position.left)).toString() }%`,
        top: `${(parseFloat(position.top)).toString() }%`,
      };
      if (this.activeFieldData) {
        pasteCoordinates = {
          // offset pasted field away from original if menu is opened from a field
          left: `${(parseFloat(this.activeFieldData.left) + this.pasteCascade).toString() }%`,
          top: `${(parseFloat(this.activeFieldData.top) + this.pasteCascade).toString() }%`,
        };
      }
      const fieldToPaste = JSON.parse(JSON.stringify(this.clipboard));
      fieldToPaste.left = pasteCoordinates.left;
      fieldToPaste.top = pasteCoordinates.top;
      fieldToPaste.page = this.currentPage;
      fieldToPaste.docIndex = this.currentDocIndex;
      // adjust paste location if it is too close to an edge
      const leftToPercentageNumber = parseInt(fieldToPaste.left, 10);
      const topToPercentageNumber = parseInt(fieldToPaste.top, 10);
      const widthToPercentageNumber = parseInt(fieldToPaste.width, 10);
      const heightToPercentageNumber = parseInt(fieldToPaste.height, 10);
      if ((leftToPercentageNumber + widthToPercentageNumber) >= 100) {
        fieldToPaste.left = `${100 - (widthToPercentageNumber + 3)}%`;
      }
      if ((topToPercentageNumber + heightToPercentageNumber) >= 100) {
        fieldToPaste.top = `${100 - (heightToPercentageNumber + 3)}%`;
      }
      // give new fields a unique temporary id to avoid duplicate key warnings - will be overridden by backend
      fieldToPaste.id = this.temporaryFieldId;
      this.temporaryFieldId++; // eslint-disable-line
      // display placeholder field while waiting for dispatch
      this.addedFields.push(fieldToPaste);
      this.$store.dispatch(`${this.storeName}/ADD_FIELDS`, {
        fields: [fieldToPaste],
        isUpdate: true,
      }).then(() => {
        // clear placeholder field
        this.addedFields = [];
      });
    },
    addField() { // Add a field to the document
      if (this.editMode) {
        const selectedField = JSON.parse(JSON.stringify(this.selectedField));

        selectedField.party = this.selectedParty;
        selectedField.page = this.currentPage;
        selectedField.docIndex = this.currentDocIndex;
        selectedField.id = this.addedFields.length;
        selectedField.title = 'Untitled';
        selectedField.required = true;
        selectedField.colour = '#000';
        selectedField.choices = [];
        selectedField.left = this.pixelToPercentage(this.roundIfSnap(selectedField.x), this.pageSize.width);
        selectedField.top = this.pixelToPercentage(this.roundIfSnap(selectedField.y), this.pageSize.height);
        this.addedFields.push(selectedField);

        this.$store.dispatch(`${this.storeName}/ADD_FIELDS`, {
          fields: [selectedField],
          isUpdate: true,
        }).then(() => {
          this.addedFields = [];
        });
      }
    },
    cutField() {
      this.logEventToAmplitude('cut');
      this.copyField();
      this.deleteField();
    },
    deleteField() {
      this.logEventToAmplitude('delete');
      // both below likely to be the same
      const field = this.configureField || this.activeFieldData;
      field.docIndex = this.currentDocIndex;
      this.$store.dispatch(`${this.storeName}/DELETE_FIELDS`, {
        fields: [field],
        isUpdate: true,
      });
      this.showConfigureField = false;
      this.resetActiveField();
    },
    roundIfSnap(value) {
      if (!this.snapMode) return value;
      return Math.round(value / 10) * 10;
    },
    closeFieldConfiguration() {
      // Broadcasted so the field will emit a validation error if there's a problem.
      this.$emit('canCloseFieldConfiguration');
      this.showConfigureField = false;
    },
    selectInputType(selected) {
      this.selectedInputType = selected;
      this.editMode = true;
      document.addEventListener('keydown', this.detectEsc);
      this.selectedField.type = selected.type;
    },
    detectEsc(e) {
      if (e.keyCode === 27) this.cancelFieldPlacement();
    },
    selectParty(selected) {
      this.selectedParty = selected;
    },
    showFields(field) { // Detect if it appropriate to show fields
      if (this.isEnvelope) {
        return (field.page == this.currentPage && this.currentDoc.contract_id == field.contract_id); // eslint-disable-line eqeqeq
      }
      return (field.page === this.currentPage);
    },
    // TODO - Feels hacky. Aim to remove this.
    forceFieldUpdate() {
      this.renderVersion += 1;
    },
    // TODO this can be added directly in to the template above as a dynamic property
    fieldOpacityStyle(field) {
      if (field.party.id !== this.selectedParty.id) return 'opacity: 0.6; pointer-events: none;';
      return '';
    },
    defaultFieldValues(field, measurement) {
      let width = 0;
      let height = 0;
      if (field.type === 'checkbox') {
        width = 20;
        height = 20;
      } else {
        width = 160;
        height = 30;
      }
      return measurement === 'height' ? height : width;
    },
    updatePageImageSize() {
      this.pageSize = {
        width: this.$refs.pageImage?.$el?.clientWidth || 0,
        height: this.$refs.pageImage?.$el?.clientHeight || 0,
      };
    },
    documentPageLoaded() {
        this.loading = false;
        // ensure layout has updated before rendering fields
        window.requestAnimationFrame(() => {
          this.updatePageImageSize();
          this.documentPageLoading = false;
        });
    },
    documentPageErrored() {
        this.loading = false;
    },
    setBoundingClientRect() {
      if (this.$refs.page) {
        const rect = this.$refs.page.getBoundingClientRect();
        this.pageRect.x = rect.left;
        this.pageRect.y = rect.top;
      }
    },
    mousedown() {
      this.events.mousedown = true;
    },
    mouseup() {
      this.events.mousedown = false;
      this.events.dragging = false;
    },
    mousemove($event) {
      if (this.editMode) {
        const field = this.selectedField;
        const elem = this.$refs.hoverField;
        if (field && elem) {
          const { scrollTop } = this.$refs.page;
          field.x = ($event.clientX - this.pageRect.x);
          field.y = (($event.clientY - this.pageRect.y) + (scrollTop - 50));
          elem.style.top = `${field.y}px`;
          elem.style.left = `${field.x}px`;
        }
      }
    },
    cancelFieldPlacement() {
      this.selectedInputType = {};
      this.editMode = false;
      this.events.newField = false;
      this.selectedField = {
        type: null,
        x: null,
        y: null,
      };
    },
    pixelToPercentage(value, basedFrom) {
      if (value) return `${(value / basedFrom) * 100}%`;
      return `${(10 / basedFrom) * 100}%`;
    },
    percentageToPixels(value, basedFrom) {
      if (value) {
        const percentage = value.replace('%', '');
        return (percentage / 100) * basedFrom;
      }
      return 20;
    },
    pixelValues(field, property) {
      if (!field[property]) {
        return null;
      }
      if (property === 'left') {
        const x = this.percentageToPixels(field.left, this.pageSize.width);
        return parseFloat(this.roundIfSnap(x));
      }
      if (property === 'top') {
        const y = this.percentageToPixels(field.top, this.pageSize.height);
        return parseFloat(this.roundIfSnap(y));
      }
      if (property === 'height') {
        const height = this.percentageToPixels(field.height, this.pageSize.height);
        if (height > 0) return parseFloat(this.roundIfSnap(height));
        return 20;
      }
      const width = this.percentageToPixels(field.width, this.pageSize.width);
      return parseFloat(this.roundIfSnap(width));
    },
    onDrag() {
      this.events.dragged = true;
      this.showConfigureField = false;
      this.fieldContextMenuActive = false;
    },
    onDragStop(x, y) { // Update field when it has stopped being dragged
      if (this.events.dragged) {
        this.showConfigureField = true;

        this.configureField.left = this.pixelToPercentage(this.roundIfSnap(x), this.pageSize.width);
        this.configureField.top = this.pixelToPercentage(this.roundIfSnap(y), this.pageSize.height);

        const field = this.configureField;
        if (this.clipboard && this.activeFieldData && this.activeFieldData.id === this.clipboard.id) {
          this.pasteCascade = 2;
          this.copyField();
        }
        field.docIndex = this.currentDocIndex;
        this.$store.dispatch(`${this.storeName}/UPDATE_FIELDS`, {
          fields: [field],
          isUpdate: true,
        });

        this.events.dragged = false;
      }
    },
    onResizing() {
      this.fieldContextMenuActive = false;
    },
    onResizeStop(x, y, width, height) { // Update field when it has stopped being resized
      this.configureField.left = this.pixelToPercentage(x, this.pageSize.width);
      this.configureField.top = this.pixelToPercentage(y, this.pageSize.height);
      this.configureField.width = this.pixelToPercentage(width, this.pageSize.width);
      this.configureField.height = this.pixelToPercentage(height, this.pageSize.height);

      const field = this.configureField;
      field.docIndex = this.currentDocIndex;
      this.$store.dispatch(`${this.storeName}/UPDATE_FIELDS`, {
        fields: [field],
        isUpdate: true,
      });
      this.showConfigureField = true;
    },
    setConfigureField(field, vm) { // Set the field currently being configured
      // pasteCascade enables pasted fields to cascade in position away from the original, by increments of 2% page width. If a new field is clicked on, this resets pasteCascade.
      if (this.currentFieldId !== field.id) {
        this.pasteCascade = 2;
        this.currentFieldId = field.id;
      }
      // activeFieldData is the actual field object. We're saving it here to use with copying and pasting.
      this.activeFieldData = field;
      this.cancelFieldPlacement();
      // configureField controls the field configuration.
      // It may be the same as activeFieldData,
      // but they are cleared at separate times so cannot be consolidated.
      this.configureField = field;
      if (vm.e.which === 1) {
        // only open field config on left click because it interferes too much with context menu
        this.doSetConfigureField();
      }
    },
    // Called when we want to set the configured field
    doSetConfigureField() {
      document.addEventListener('keydown', this.detectEsc);
      this.showConfigureField = true;
    },
    changePage(page) {
      if (page !== this.currentPage) {
        this.documentPageLoading = true;
        this.currentPage = page;
        this.pasteCascade = 2;
      }
    },
    nextDocument() { // Switch to the next document in an envelope
      if ((this.currentDocIndex + 1) < this.doc.documents.length) {
        this.documentPageLoading = true;
        this.currentDocIndex += 1;
        this.currentDoc = this.doc.documents[this.currentDocIndex];
        this.currentPage = 0;
      }
    },
    previousDocument() { // Switch to the previous document in an envelope
      if (this.currentDocIndex > 0) {
        this.documentPageLoading = true;
        this.currentDocIndex -= 1;
        this.currentDoc = this.doc.documents[this.currentDocIndex];
        this.currentPage = 0;
      }
    },
    // Update field configuration
    updatedConfiguration({ field, changed = false }) {
      let found = false; // Checks it's not been deleted
      this.fields.forEach((item) => {
        if (field.id === item.id) {
          Object.assign(item, field);
          found = true;
        }
      });
      if (changed && found) {
        const updatedField = JSON.parse(JSON.stringify(field));
        updatedField.docIndex = this.currentDocIndex;
        this.$store.dispatch(`${this.storeName}/UPDATE_FIELDS`, {
          fields: [updatedField],
          isUpdate: true,
        });
      }
    },
    // Save a new title (for templates only)
    saveNewTitle({ title }) {
      const template = this.doc;
      template.title = title;
      template.id = template.fingerprint;

      this.$store.dispatch('template/UPDATE', template);
    },
    // Send (for envelopes only)
    sendEnvelope() {
      this.send();
    },
  },
};
</script>

<style scoped>
.document-setup {
  background: #fff;
  height: 100%;
  overflow: hidden;
}

.document-setup:after {
  content: '';
  display: block;
  clear: both;
}

.document-setup__header {
  padding: var(--spacing-base);
  margin-bottom: 0;
  border-bottom: 1px solid var(--color-border);
  background: #fff;
}

.document-setup__header:after {
  content: '';
  display: block;
  clear: both;
}

.document-setup__header h2 {
  line-height: 38px;
  margin-bottom: 0;
  display: inline-block;
  vertical-align: middle;
  width: 70vw;
  max-width: calc(100% - 220px);
  white-space: nowrap;
  overflow: visible;
  text-overflow: ellipsis;
}

.document-setup__header--envelope h2 {
  display: inline-block;
  width: auto;
  max-width: calc(100% - 470px);
}

.document-setup__header__back-button {
  margin-right: var(--spacing-base);
}

.document-setup__header__updated {
  display: inline-block;
  font-size: 11px;
  text-align: right;
}

.document-setup__header__action-button {
  display: inline-block;
  margin-left: var(--spacing-base);
}

.document-setup__field-control {
  position: absolute;
  top: var(--spacing-large);
  right: var(--spacing-large);
  width: 50px;
  height: 50px;
  z-index: 3;
  background: var(--color-error-dark);
  border: 3px solid #bf2718;
  border-radius: 50%;
  color: white;
  text-align: center;
  box-shadow: 0 0 2px 1px rgba(0, 0, 0, 0.2);
  transition: 0.2s ease all;
}

.document-setup__field-control svg {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.document-setup__field-control:hover {
  cursor: pointer;
  transform: scale(1.1);
}

.document-setup__field-control:hover .document-setup__field-control__name {
  display: block;
}

.document-setup__field-control__name {
  position: absolute;
  color: #fff;
  background: #222;
  padding: var(--spacing-xs) var(--spacing-small);
  font-size: 12px;
  font-weight: bold;
  border-radius: 30px;
  display: none;
  z-index: 9;
  position: absolute;
  transform: translate3d(0, -50%, 0);
  top: 50%;
}

.document-setup__field-control__name--left {
  left: 100%;
  margin-left: var(--spacing-base);
  white-space: nowrap;
}

.document-setup__field-control__name--right {
  right: 100%;
  margin-right: var(--spacing-base);
}

.document-setup__container {
  height: calc(100% - 69px);
  display: flex;
}

.document-setup__envelope-nav {
  display: inline-block;
  vertical-align: middle;
  padding-left: 15px;
  margin-left: 15px;
  border-left: 1px solid var(--color-border);
}

.document-setup__envelope-nav span {
  margin: 0 var(--spacing-xs);
}

.document-setup__envelope-nav__icon {
  display: inline-block;
  vertical-align: middle;
  float: none;
  height: 24px;
  width: 24px;
}

.document-setup__envelope-nav__icon svg {
  height: 8px;
  width: 8px;
  position: absolute;
  transform: translate3d(-50%, -50%, 0);
  top: 50%;
  left: 50%;
}

.document-setup__envelope-nav__text {
  vertical-align: middle;
}

.document-setup__content {
  background: var(--signable-secondary-2);
  position: relative;
  display: inline-flex;
  align-content: stretch;
  flex-direction: column;
  height: 100%;
  flex-basis: 100%;
  margin: 0 auto;
  border-right: 1px solid #253843;
  border-left: 1px solid #253843;
}

.document-setup__content, .document-setup__pages {
  height: 100%;
}

.document-setup__pages {
  width: 80%;
  margin: 0 auto 0;
  position: relative;
  -webkit-margin-collapse: separate;
  height: 100%;
  text-align: center;
  flex-shrink: 1;
  min-height: 0;
}

.document-setup__field-config {
  width: 100%;
  flex-grow: 1;
  z-index: 3;
  bottom: 0px;
}

.document-setup__image {
  box-shadow: 0 0 5px -2px #222;
  border-radius: 8px;
  margin-bottom: var(--spacing-large);
  position: relative;
}

.document-setup__image .a-loading-spinner {
  min-height: 480px;
}

.document-setup__field-container {
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
}

.document-setup__navigation {
  padding: 50px var(--spacing-xs) var(--spacing-large) 0;
  display: inline-block;
  height: 100%;
  overflow-y: auto;
  width: calc(8% + 30px);
  margin-left: -35px;
  position: relative;
  z-index: 0;
  background: var(--signable-secondary-2);
  text-align: left;
  padding-right: 30px;
  margin-right: -30px;
}

.document-setup__navigation:after {
  content: '';
  height: 100%;
  width: 20px;
  right: 0;
  background: red;
}

.document-setup__navigation__image {
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
  margin-top: var(--spacing-base);
}

.document-setup__navigation__image img {
  border: 3px solid var(--signable-secondary-4);
  box-shadow: 0 0 5px -2px #222;
  border-radius: 6px;
}

.document-setup__navigation__image[disabled] {
  cursor: default;
}

.document-setup__navigation__image--active img {
  border: 3px solid var(--signable-primary-1);
}

.document-setup__navigation__image:nth-of-type(1) {
  margin-top: 0;
}

.document-setup__page {
  padding: 0;
  position: relative;
  overflow-y: auto;
  height: 100%;
  width: calc(100% - 8% - 30px);
  display: inline-block;
  vertical-align: top;
  padding-top: 50px;
  margin-right: 50px;
  padding-right: 30px;
}

.document-setup__page img {
  border-radius: 9px;
  pointer-events: none;
}

.document-setup__page__snap-control {
  position: absolute;
  top: 20px;
  right: 30px;
}

.document-setup__page__snap-control .a-checkbox {
  vertical-align: middle;
}

.document-setup__page__snap-control__label {
  vertical-align: middle;
  color: #fff;
  font-size: var(--font-size-small);
  margin-right: var(--spacing-xs);
  font-weight: bold;
}

.document-setup__page__field, .document-setup__page__hover-field {
  position: absolute;
  background: #fff;
  border: 2px solid;
  z-index: 1;
  border-radius: 3px;
}

.document-setup__page__field__adding-label, .document-setup__page__hover-field__adding-label {
  font-size: 12px;
}

.document-setup__page__field[data-type="text"], .document-setup__page__hover-field[data-type="text"] {
  border-color: var(--color-text-field);
}

.document-setup__page__field[data-type="text"] .document-setup__page__field__label, .document-setup__page__hover-field[data-type="text"] .document-setup__page__field__label {
  background: var(--color-text-field);
}

.document-setup__page__field[data-type="signature"], .document-setup__page__hover-field[data-type="signature"] {
  border-color: var(--color-signature-field);
}

.document-setup__page__field[data-type="signature"] .document-setup__page__field__label, .document-setup__page__hover-field[data-type="signature"] .document-setup__page__field__label {
  background: var(--color-signature-field);
}

.document-setup__page__field[data-type="date"], .document-setup__page__hover-field[data-type="date"] {
  border-color: var(--color-date-field);
}

.document-setup__page__field[data-type="date"] .document-setup__page__field__label, .document-setup__page__hover-field[data-type="date"] .document-setup__page__field__label {
  background: var(--color-date-field);
}

.document-setup__page__field[data-type="dropdown"], .document-setup__page__hover-field[data-type="dropdown"] {
  border-color: var(--color-dropdown-field);
}

.document-setup__page__field[data-type="dropdown"] .document-setup__page__field__label, .document-setup__page__hover-field[data-type="dropdown"] .document-setup__page__field__label {
  background: var(--color-dropdown-field);
}

.document-setup__page__field[data-type="checkbox"], .document-setup__page__hover-field[data-type="checkbox"] {
  border-color: var(--color-checkbox-field);
  min-width: 20px;
  min-height: 20px;
  padding: 0;
}

.document-setup__page__field[data-type="checkbox"] .document-setup__page__field__label, .document-setup__page__hover-field[data-type="checkbox"] .document-setup__page__field__label {
  background: var(--color-checkbox-field);
}

.document-setup__page__field[data-type="checkbox"] span, .document-setup__page__hover-field[data-type="checkbox"] span {
  display: none;
}

.document-setup__page__field[data-type="upload"], .document-setup__page__hover-field[data-type="upload"] {
  border-color: var(--color-upload-field);
}

.document-setup__page__field[data-type="upload"] .document-setup__page__field__label, .document-setup__page__hover-field[data-type="upload"] .document-setup__page__field__label {
  background: var(--color-upload-field);
}

.document-setup__page__hover-field {
  pointer-events: none;
  box-shadow: 0px 0px 15px -5px #000;
  z-index: 2;
  width: 155px;
  height: 25px;
}

.document-setup__page__hover-field--checkbox {
  width: 20px;
  height: 20px;
}

.document-setup__page__field {
  padding-left: var(--spacing-xs);
  box-shadow: none;
  transition: box-shadow 0.25s ease;
}

.document-setup__page__field.active {
  cursor: move;
  box-shadow: 2px 2px 15px -5px black;
}

.document-setup__page__field__required-marker {
  border-radius: 50%;
  height: 8px;
  width: 8px;
  background: #e74c3c;
  position: absolute;
  top: -4px;
  right: -4px;
}

.document-setup__page__field__checkbox-group-marker {
  height: 30px;
  width: 30px;
  display: none;
}

.document-setup__page__field__label {
  position: absolute;
  top: calc(0px - var(--spacing-base));
  color: #fff;
  left: -2px;
  font-size: 11px;
  color: #fff;
  padding: 2px 4px;
  line-height: 1;
  border-radius: 3px;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 3px;
  white-space: nowrap;
  font-weight: bold;
}

.document-setup__page__field span {
  pointer-events: none;
}

.document-setup__page__field--loading {
  border-color: var(--signable-secondary-4) !important;
  pointer-events: none !important;
}

.document-setup__page__field--loading .document-setup__page__field__label {
  background: var(--signable-secondary-4) !important;
}

.document-setup__field {
  color: #fff;
  background: var(--signable-secondary-2);
  border: 2px solid #253843;
  height: 52px;
  width: 52px;
  border-radius: 30px;
  position: relative;
  margin: 0 auto var(--spacing-base);
  cursor: pointer;
  transition: transform .05s ease;
  z-index: 0;
  position: relative;
}

.document-setup__field:hover {
  z-index: 3;
}

.document-setup__field:hover .document-setup__field__name {
  display: block;
}

.document-setup__field:hover, .document-setup__field--selected {
  transform: scale(1.2);
}

.document-setup__field[data-type="text"] {
  background-color: var(--color-text-field);
  border: 2px solid #c29d0b;
}

.document-setup__field[data-type="signature"] {
  background-color: var(--color-signature-field);
  border: 2px solid #107360;
}

.document-setup__field[data-type="date"] {
  background-color: var(--color-date-field);
  border: 2px solid #804399;
}

.document-setup__field[data-type="dropdown"] {
  background-color: var(--color-dropdown-field);
  border: 2px solid #667273;
}

.document-setup__field[data-type="checkbox"] {
  background-color: var(--color-checkbox-field);
  border: 2px solid #962d22;
}

.document-setup__field[data-type="upload"] {
  background-color: var(--color-upload-field);
  border: 2px solid #bf6516;
}

.document-setup__field[data-type="cancel"] {
  background-color: var(--color-error-dark);
  border: 2px solid #bf2718;
}

.document-setup__field[data-type="send"] {
  background-color: var(--color-success-dark);
  border: 2px solid #19703e;
}

.document-setup__field__name {
  position: absolute;
  color: #fff;
  background: #222;
  padding: var(--spacing-xs) var(--spacing-small);
  font-size: 12px;
  font-weight: bold;
  border-radius: 30px;
  display: none;
  z-index: 9;
  position: absolute;
  transform: translate3d(0, -50%, 0);
  top: 50%;
}

.document-setup__field__name--left {
  left: 100%;
  margin-left: var(--spacing-base);
  white-space: nowrap;
}

.document-setup__field__name--right {
  right: 100%;
  margin-right: var(--spacing-base);
}

.document-setup__field svg,
.document-setup__field span {
  pointer-events: none;
  position: absolute;
  transform: translate3d(-50%, -50%, 0);
  top: 50%;
  left: 50%;
}

.document-setup__field span {
  font-weight: bold;
}

.document-setup__field {
  color: #fff;
}

.document-setup__field__separator {
  border-top: 1px solid var(--color-border);
  margin-bottom: var(--spacing-base);
}

.document-setup--edit-mode {
  cursor: crosshair;
}

.slide-enter-active,
.slide-leave-active {
  transition: transform .25s, opacity .25s;
}

.slide-enter,
.slide-leave-to {
  /* .fade-leave-active below version 2.1.8 */
  transform: translate3d(0, 100%, 0);
  flex-grow: 0.001;
  opacity: 0;
}

.document-setup__page-spinner {
  position: absolute;
  width: calc(100% - 30px);
  top: 25%;
}
</style>
